/**
 * Payer Connections — the AC-2 freshness line.
 *
 * Pure and deterministic: `now` is always an explicit parameter, never
 * `Date.now()`, so tests and stories can pin it. The passthrough's
 * `checked_at` is a tz-aware UTC datetime (the handler builds it with
 * `datetime.now(timezone.utc)`), so Pydantic serializes it with a `+00:00`
 * offset and `new Date()` parses it as an instant, not as local wall time.
 */

/** AC-2 line prefix, verbatim. */
export const CHECKED_AT_PREFIX = 'Checked with the clearinghouse at';

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Format `checked_at` for the AC-2 line in the viewer's locale: the time
 * alone when the read happened today, otherwise a short date and the time.
 * "Today" is decided on local calendar parts, so a 23:50 UTC read is
 * "today" for a viewer whose local date matches, not for one whose doesn't.
 *
 * Returns null for a missing or unparseable value — the caller drops the
 * line rather than rendering "Invalid Date".
 */
export function formatCheckedAt(
  checkedAt: string | null | undefined,
  now: Date,
  locales?: string | string[],
): string | null {
  if (!checkedAt) return null;
  const at = new Date(checkedAt);
  if (Number.isNaN(at.getTime())) return null;

  const time = at.toLocaleTimeString(locales, { hour: 'numeric', minute: '2-digit' });
  if (isSameLocalDay(at, now)) return time;

  const date = at.toLocaleDateString(locales, { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}

/** The whole AC-2 line, or null when there is no usable timestamp. */
export function checkedAtLine(
  checkedAt: string | null | undefined,
  now: Date,
  locales?: string | string[],
): string | null {
  const stamp = formatCheckedAt(checkedAt, now, locales);
  return stamp === null ? null : `${CHECKED_AT_PREFIX} ${stamp}`;
}
